import React from 'react';
import { clearGameState, getLocalState, setLocalState } from '../../localState';
import { Question, RoundState, Team } from '../Game/Game';
import { Col, Fill, Row } from '../../components/RowCol';
import Spacer from '../../components/Spacer';
import NumberEditor from '../../components/NumberEditor';
import QuestionControls from './QuestionControls';
import BigBoard from '../../components/BigBoard/BigBoard';
import ConfirmableButton from '../../components/ConfirmableButton';
import LocalAudio from '../../components/LocalAudio';
import LocalFileStorage from '../../LocalFileStorage';

type Props = {};
type State = {};

class ControlPanel extends React.Component<Props, State> {
	readonly db: LocalFileStorage;

	constructor(props: Props) {
		super(props);

		this.db = new LocalFileStorage();
	}

	storageUpdate = () => this.forceUpdate();

	componentDidMount() {
		this.db.init().then(() => this.forceUpdate());

		window.addEventListener('storage', this.storageUpdate);
	}

	componentWillUnmount() {
		window.removeEventListener('storage', this.storageUpdate);
	}

	reset = () => {
		clearGameState();
		this.forceUpdate();
	};

	getSelectedQuestion = (): {
		selectedQuestion: Question | null,
		selectedQuestionLevel: number | null,
	} => {
		const {
			currentRoundIndex,
			selectedQuestionIndex,
			rounds,
		} = getLocalState();
		const currentRound = rounds[currentRoundIndex];

		if ('finalJeopardy' in currentRound) {
			return {
				selectedQuestion: currentRound.question,
				selectedQuestionLevel: null,
			};
		}
		if (selectedQuestionIndex === null) {
			return {
				selectedQuestion: null,
				selectedQuestionLevel: null,
			};
		}
		const [categoryIndex, questionIndex] = selectedQuestionIndex;
		const question = currentRound.categories[categoryIndex].questions[questionIndex];
		return {
			selectedQuestion: question,
			selectedQuestionLevel: currentRound.levels[questionIndex],
		};
	};

	setRound = (roundIndex: number) => {
		const { rounds } = getLocalState();
		const round = rounds[roundIndex];
		if ('finalJeopardy' in round) {
			const roundState: RoundState = [[false]];
			setLocalState('currentRoundIndex', roundIndex);
			setLocalState('selectedQuestionIndex', null);
			setLocalState('roundState', roundState);
			setLocalState('dailyDoubleWager', null);
			setLocalState('finalJeopardyWagers', null);
			this.forceUpdate();
		} else {
			const roundState: RoundState = round.categories.map(
				category => category.questions.map(() => false),
			);
			setLocalState('currentRoundIndex', roundIndex);
			setLocalState('selectedQuestionIndex', null);
			setLocalState('roundState', roundState);
			setLocalState('dailyDoubleWager', null);
			setLocalState('finalJeopardyWagers', null);
			this.forceUpdate();
		}
	};

	editScore = (team: Team, newScore: number) => {
		const { scores } = getLocalState();

		const newScores = {
			...scores,
			[team]: newScore,
		};

		setLocalState('scores', newScores);
		this.forceUpdate();
	};

	selectQuestion = (categoryIndex: number, questionIndex: number) => {
		setLocalState('selectedQuestionIndex', [categoryIndex, questionIndex]);
		this.forceUpdate();
	};

	setDailyDoubleWager = (wager: number) => {
		setLocalState('dailyDoubleWager', wager);
		this.forceUpdate();
	};

	setFinalJeopardyWagers = (wagers: [number, number]) => {
		setLocalState('finalJeopardyWagers', wagers);
		this.forceUpdate();
	};

	resolveQuestion = (winningTeam: Team | null) => {
		const {
			scores,
			currentRoundIndex,
			selectedQuestionIndex,
			roundState,
			rounds,
		} = getLocalState();
		if (selectedQuestionIndex === null) return;
		const [categoryIndex, questionIndex] = selectedQuestionIndex;

		let newScores = scores;
		if (winningTeam !== null) {
			const currentRound = rounds[currentRoundIndex];
			if (!('finalJeopardy' in currentRound)) {
				const level = currentRound.levels[questionIndex];
				newScores = {
					...scores,
					[winningTeam]: scores[winningTeam] + level,
				};
			}
		}

		const newRoundState = roundState.map(categoryState => categoryState.slice());
		newRoundState[categoryIndex][questionIndex] = true;

		setLocalState('scores', newScores);
		setLocalState('roundState', newRoundState);
		setLocalState('selectedQuestionIndex', null);
		this.forceUpdate();
	};

	resolveDailyDouble = (team: Team, points: number) => {
		const {
			scores,
			selectedQuestionIndex,
			roundState,
		} = getLocalState();
		if (selectedQuestionIndex === null) return;
		const [categoryIndex, questionIndex] = selectedQuestionIndex;

		const newScores = {
			...scores,
			[team]: scores[team] + points,
		};

		const newRoundState = roundState.map(categoryState => categoryState.slice());
		newRoundState[categoryIndex][questionIndex] = true;

		setLocalState('scores', newScores);
		setLocalState('roundState', newRoundState);
		setLocalState('selectedQuestionIndex', null);
		setLocalState('dailyDoubleWager', null);
		this.forceUpdate();
	};

	resolveFinalJeopardy = (team: Team, points: number) => {
		const { scores } = getLocalState();
		const newScores = {
			...scores,
			[team]: scores[team] + points,
		};
		const newRoundState = [[true]];

		setLocalState('scores', newScores);
		setLocalState('roundState', newRoundState);
		this.forceUpdate();
	};

	render() {
		const {
			rounds, currentRoundIndex, roundState, scores,
			dailyDoubleWager, finalJeopardyWagers,
		} = getLocalState();
		const currentRound = rounds[currentRoundIndex];
		const { selectedQuestion, selectedQuestionLevel } = this.getSelectedQuestion();

		return (
			<Fill style={{
				padding: '50px',
				color: 'white',
			}}>
				<Col fill alignCenter>
					<ConfirmableButton
						label="Reset Entire Game"
						onClick={this.reset}
					/>

					<Spacer vertical={50} />

					{(['A', 'B'] as Team[]).map(team => (
						<React.Fragment
							key={team}
						>
							<NumberEditor
								label={`Edit Team ${team} Score:`}
								number={scores[team]}
								edit={newScore => this.editScore(team, newScore)}
							/>
							{team === 'A' && <Spacer vertical={10} />}
						</React.Fragment>
					))}

					<Spacer vertical={50} />

					<Col alignCenter style={{
						height: '450px',
					}}>
						{selectedQuestion && (
							<>
								<Spacer vertical={20} />
								<div style={{
									fontSize: '24px',
									color: 'white',
									width: '700px',
									textAlign: 'center',
								}}>
									{selectedQuestion.text}
								</div>
								<Spacer vertical={20} />
							</>
						)}

						{selectedQuestion && selectedQuestion.answer && (
							<>
								<Spacer vertical={40} />
								<Row style={{
									fontFamily: 'Korinna',
									fontSize: '24px',
									color: 'white',
									textAlign: 'center',
								}}>
									<div>ANSWER:&nbsp;</div>
									<div style={{
										fontStyle: 'italic',
									}}>{selectedQuestion.answer}</div>
								</Row>
								<Spacer vertical={40} />
							</>
						)}

						{selectedQuestion && 'audio' in selectedQuestion && (
							<Row alignCenter style={{
								height: '70px',
							}}>
								<div style={{
									fontFamily: 'Korinna',
									textTransform: 'uppercase',
									fontSize: '24px',
									color: 'white',
								}}>
									Audio Question:
								</div>
								<Spacer horizontal={20} />
								<LocalAudio
									localFileStorage={this.db}
									dbKey={selectedQuestion.audio}
									style={{
										height: '20px',
									}}
									controls
								/>
							</Row>
						)}

						<Spacer vertical={50} />

						{selectedQuestion && selectedQuestionLevel ? (
							<Row alignCenter style={{
								height: '70px',
								width: '700px',
								justifyContent: 'space-around',
							}}>
								<QuestionControls
									question={selectedQuestion}
									dailyDoubleWager={dailyDoubleWager}
									questionLevel={selectedQuestionLevel}
									setDailyDoubleWager={this.setDailyDoubleWager}
									finalJeopardy={false}
									finalJeopardyWagers={finalJeopardyWagers}
									setFinalJeopardyWagers={this.setFinalJeopardyWagers}
									resolveQuestion={this.resolveQuestion}
									resolveDailyDouble={this.resolveDailyDouble}
									resolveFinalJeopardy={this.resolveFinalJeopardy}
								/>
							</Row>
						) : (
							('finalJeopardy' in currentRound ? (
								<Col>
									<QuestionControls
										question={currentRound.question}
										dailyDoubleWager={dailyDoubleWager}
										setDailyDoubleWager={this.setDailyDoubleWager}
										finalJeopardy={true}
										finalJeopardyWagers={finalJeopardyWagers}
										setFinalJeopardyWagers={this.setFinalJeopardyWagers}
										resolveQuestion={this.resolveQuestion}
										resolveDailyDouble={this.resolveDailyDouble}
										resolveFinalJeopardy={this.resolveFinalJeopardy}
									/>
								</Col>
							) : (
								<div style={{
									position: 'relative',
									transform: 'scale(0.5)',
									transformOrigin: 'top center',
								}}>
									<BigBoard
										categories={currentRound.categories}
										levels={currentRound.levels}
										roundState={roundState}
										selectQuestion={this.selectQuestion}
									/>
								</div>
							))
						)}
					</Col>

					<Spacer vertical={50} />

					{currentRoundIndex === 0 && (
						<ConfirmableButton
							label="Go to Double Jeopardy"
							onClick={() => this.setRound(1)}
						/>
					)}

					{currentRoundIndex === 1 && (
						<Row>
							<ConfirmableButton
								label="Go Back to Single Jeopardy"
								onClick={() => this.setRound(0)}
							/>
							<Spacer horizontal={50} />
							<ConfirmableButton
								label="Go to Final Jeopardy"
								onClick={() => this.setRound(2)}
							/>
						</Row>
					)}

					{currentRoundIndex === 2 && (
						<Row>
							<ConfirmableButton
								label="Go Back to Double Jeopardy"
								onClick={() => this.setRound(1)}
							/>
							{/*
							<Spacer horizontal={50} />
							<ConfirmableButton
								label="End Game"
								onClick={() => this.setRound(2)}
							/>
							*/}
						</Row>
					)}
				</Col>
			</Fill>
		);
	}
}

export default ControlPanel;
